import { HttpClient } from '@angular/common/http';
import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Subject, from, of } from 'rxjs';
import { takeUntil, mergeMap, catchError } from 'rxjs/operators';
import { PdfSecurityService } from 'src/app/services/pdfsecurity.service';
import {
  ExcelValidationResult,
  TransformedPresignedUrlsData,
  TransformedPresignedUrl,
  FileUploadStatus,
} from '../bulk-upload.interfaces';
import { UploadProgressService } from 'src/app/services/upload-progress.service';
import { TableColumn } from 'src/app/shared-components/data-table/data-table.component';

@Component({
  selector: 'app-bulk-upload-estamp',
  templateUrl: './bulk-upload-estamp.component.html',
  styleUrls: ['./bulk-upload-estamp.component.scss'],
  standalone: false,
})
export class BulkUploadEstampComponent implements OnInit, OnDestroy {
  readonly fileColumns: TableColumn[] = [
    { field: 's_no', header: '#', width: '50px' },
    { field: 'file_name', header: 'File Name' },
    {
      field: 'size',
      header: 'Size',
      width: '90px',
      accessorFn: (row) => this.getFileSizeDisplay(row),
      cellStyle: { 'font-size': '12px', color: '#6c757d' },
    },
  ];

  readonly fileRowClass = (fileName: string): string => {
    const status = this.getFileStatus(fileName);
    if (status === 'valid') return 'table-success';
    if (status === 'invalid') return 'table-danger';
    if (status === 'validating') return 'table-warning';
    return '';
  };

  expectedFileNames: string[] = [];
  presignedUrlsMap: Map<string, TransformedPresignedUrl> = new Map();
  sessionId: string = '';
  selectedFiles: File[] = [];
  fileValidationStatus: Map<
    string,
    'pending' | 'validating' | 'valid' | 'invalid'
  > = new Map();
  fileValidationErrors: Map<string, string> = new Map();

  validationErrors: string[] = [];
  isFilesSelected: boolean = false;
  isValidatingFiles: boolean = false;

  // NEW: track already-signed skipped files
  skippedSignedFiles: string[] = [];

  // File size and page limits
  maxFileSizeMB = 15;
  maxFileSizeBytes = 15 * 1024 * 1024;
  maxPagesPerPDF = 50;
  selectedServiceType: 'eStamp' | 'eSign' | 'both' | '' = '';

  // Single unified files grid — filter chip + search, both display-only, never
  // affect matching/validation logic (all of that reads expectedFileNames /
  // selectedFiles directly, never this filtered/paginated view).
  pageSize = 50;
  pageFirstIndex = 0;
  fileFilterMode: 'all' | 'missing' | 'added' | 'issues' = 'all';
  fileSearchText = '';

  private selectedFileSizeMap: Map<string, number> = new Map();
  private expectedFileNamesSet: Set<string> = new Set();

  private destroy$ = new Subject<void>();
  private uploadedFiles: FileUploadStatus[] = [];

  constructor(
    private http: HttpClient,
    private router: Router,
    private messageService: MessageService,
    private pdfSecurityService: PdfSecurityService,
    private uploadProgressService: UploadProgressService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadSessionData();
    this.setupRetryHandler();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get missingFileNames(): string[] {
    return this.expectedFileNames.filter(
      (name) => !this.selectedFileSizeMap.has(name),
    );
  }

  get unexpectedFiles(): File[] {
    return this.selectedFiles.filter(
      (f) => !this.expectedFileNamesSet.has(f.name),
    );
  }

  get collectedCount(): number {
    return this.addedFileNames.length;
  }

  getFileError(fileName: string): string | null {
    return this.fileValidationErrors.get(fileName) || null;
  }

  getFileStatus(fileName: string): string {
    return this.fileValidationStatus.get(fileName) || 'pending';
  }

  get addedFileNames(): string[] {
    return this.expectedFileNames.filter((name) =>
      this.selectedFileSizeMap.has(name),
    );
  }

  get issuesFileNames(): string[] {
    return this.expectedFileNames.filter(
      (name) => this.fileValidationStatus.get(name) === 'invalid',
    );
  }

  get collectedPercent(): number {
    return this.expectedFileNames.length
      ? Math.round((this.collectedCount / this.expectedFileNames.length) * 100)
      : 0;
  }

  // The list backing the grid, after filter chip + search — display only.
  get filteredFileNames(): string[] {
    let list: string[];
    switch (this.fileFilterMode) {
      case 'missing':
        list = this.missingFileNames;
        break;
      case 'added':
        list = this.addedFileNames;
        break;
      case 'issues':
        list = this.issuesFileNames;
        break;
      default:
        list = this.expectedFileNames;
    }
    const query = this.fileSearchText.trim().toLowerCase();
    if (query) {
      list = list.filter((name) => name.toLowerCase().includes(query));
    }
    return list;
  }

  get pagedFileNames(): string[] {
    return this.filteredFileNames.slice(
      this.pageFirstIndex,
      this.pageFirstIndex + this.pageSize,
    );
  }

  setFileFilter(mode: 'all' | 'missing' | 'added' | 'issues'): void {
    this.fileFilterMode = mode;
    this.pageFirstIndex = 0;
  }

  onFileSearchChange(value: string): void {
    this.fileSearchText = value ?? '';
    this.pageFirstIndex = 0;
  }

  onFileListPageChange(event: any): void {
    this.pageFirstIndex = event.first;
    this.pageSize = event.rows;
  }

  private clampPageFirstIndex(): void {
    const total = this.filteredFileNames.length;
    const maxFirst =
      Math.max(0, Math.ceil(total / this.pageSize) - 1) * this.pageSize;
    if (this.pageFirstIndex > maxFirst) this.pageFirstIndex = maxFirst;
  }

  getFileSizeDisplay(fileName: string): string {
    const size = this.selectedFileSizeMap.get(fileName);
    return size !== undefined ? this.formatFileSize(size) : '';
  }

  private loadSessionData(): void {
    const validationData = sessionStorage.getItem('excelValidationData');
    const presignedUrlsData = sessionStorage.getItem('presignedUrlsData');

    if (!validationData || !presignedUrlsData) {
      this.messageService.add({
        severity: 'error',
        summary: 'No Data Found',
        detail: 'Please complete Excel upload first',
      });
      this.router.navigate(['/doqfy/bulk-upload-estamp/excel-upload']);
      return;
    }

    try {
      const validation: ExcelValidationResult = JSON.parse(validationData);
      const urlsData: TransformedPresignedUrlsData =
        JSON.parse(presignedUrlsData);

      this.selectedServiceType = (sessionStorage.getItem(
        'selectedServiceType',
      ) ?? '') as 'eStamp' | 'eSign' | 'both' | '';

      this.expectedFileNames = validation.fileNames || [];
      this.expectedFileNamesSet = new Set(this.expectedFileNames);
      this.pageFirstIndex = 0;
      this.fileFilterMode = 'all';
      this.fileSearchText = '';
      this.sessionId = urlsData.session_id;

      urlsData.presigned_urls.forEach((urlData: TransformedPresignedUrl) => {
        this.presignedUrlsMap.set(urlData.file_name, urlData);
      });

      if (this.expectedFileNames.length === 0) {
        throw new Error('No file names found in validation data');
      }

      if (this.presignedUrlsMap.size !== this.expectedFileNames.length) {
        throw new Error(
          `Mismatch: Expected ${this.expectedFileNames.length} presigned URLs but got ${this.presignedUrlsMap.size}`,
        );
      }

      this.messageService.add({
        severity: 'info',
        summary: 'Ready for Upload',
        detail: `Select ${this.expectedFileNames.length} PDF file(s) to upload`,
      });
    } catch (error: any) {
      console.error('Initialization error:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Data Error',
        detail: error.message || 'Failed to load data',
      });
      this.router.navigate(['/doqfy/bulk-upload-estamp/excel-upload']);
    }
  }

  async onFileSelect(event: any) {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    const newlyPicked: File[] = Array.from(files);
    event.target.value = '';

    newlyPicked.forEach((newFile) => {
      const existingIndex = this.selectedFiles.findIndex(
        (f) => f.name === newFile.name,
      );
      if (existingIndex !== -1) {
        this.selectedFiles[existingIndex] = newFile;
        this.fileValidationStatus.set(newFile.name, 'pending');
        this.fileValidationErrors.delete(newFile.name);
      } else {
        this.selectedFiles.push(newFile);
      }
      this.selectedFileSizeMap.set(newFile.name, newFile.size);
    });

    await this.validateNewFiles(newlyPicked);
  }

  // CHANGED: handles 'already_signed' sentinel from validateSingleFile
  private async validateNewFiles(filesToValidate: File[]) {
    this.isValidatingFiles = true;
    this.validationErrors = [];


    const cdInterval = Math.max(1, Math.ceil(filesToValidate.length / 40));

    try {
      let processedCount = 0;
      for (const file of filesToValidate) {
        this.fileValidationStatus.set(file.name, 'validating');
        processedCount++;
        if (processedCount % cdInterval === 0) {
          this.cdr.detectChanges();
        }

        const result = await this.validateSingleFile(file);

        if (result === 'already_signed') {
          // Auto-remove from everything so order proceeds without it
          this.expectedFileNames = this.expectedFileNames.filter(
            (n) => n !== file.name,
          );
          this.expectedFileNamesSet.delete(file.name);
          this.presignedUrlsMap.delete(file.name);
          this.selectedFiles = this.selectedFiles.filter(
            (f) => f.name !== file.name,
          );
          this.selectedFileSizeMap.delete(file.name);
          this.fileValidationStatus.delete(file.name);
          this.fileValidationErrors.delete(file.name);
          this.clampPageFirstIndex();

          if (!this.skippedSignedFiles.includes(file.name)) {
            this.skippedSignedFiles.push(file.name);
          }

          this.messageService.add({
            severity: 'warn',
            summary: 'File Skipped',
            detail: `"${file.name}" is already signed and has been removed. Order will proceed without it.`,
            life: 8000,
          });
        } else if (result !== null) {
          this.fileValidationStatus.set(file.name, 'invalid');
          this.fileValidationErrors.set(file.name, result);
        } else {
          this.fileValidationStatus.set(file.name, 'valid');
          this.fileValidationErrors.delete(file.name);
        }
      }

      this.evaluateOverallValidity();
    } catch (error: any) {
      this.validationErrors.push(error.message || 'Validation error occurred');
      this.isFilesSelected = false;
    } finally {
      this.isValidatingFiles = false;
      this.cdr.detectChanges();
    }
  }

  // CHANGED: returns 'already_signed' sentinel | error string | null
  private async validateSingleFile(file: File): Promise<string | null> {
    // 1. Must be in the expected list
    if (!this.expectedFileNamesSet.has(file.name)) {
      return `"${file.name}" is not in the expected file list`;
    }

    // 2. Must be a PDF
    if (file.type !== 'application/pdf') {
      return `"${file.name}" is not a PDF file`;
    }

    // 3. Size check
    if (file.size > this.maxFileSizeBytes) {
      return `"${file.name}" exceeds ${this.maxFileSizeMB}MB limit (${this.formatFileSize(file.size)})`;
    }

    // 4. Presigned URL must exist
    if (!this.presignedUrlsMap.has(file.name)) {
      return `No upload URL found for "${file.name}"`;
    }

    // 5. Already-signed check at selection time (only for eSign and both)
    if (
      this.selectedServiceType === 'eSign' ||
      this.selectedServiceType === 'both'
    ) {
      try {
        const validation =
          await this.pdfSecurityService.validateAndSanitizePDF(file);
        if (validation.isESigned) {
          return 'already_signed';
        }
        if (!validation.isValid) {
          return validation.reason || `"${file.name}" failed PDF validation`;
        }
      } catch (e: any) {
        return `Unable to validate "${file.name}": ${e.message}`;
      }
    }

    return null; // valid
  }

  /**
   * After every validation pass, decides whether ALL expected files are present
   * and individually valid — only then enables the Start Upload button.
   */
  private evaluateOverallValidity() {
    const missingFiles = this.missingFileNames;
    const filesWithErrors: string[] = [];

    this.selectedFiles.forEach((f) => {
      if (this.fileValidationErrors.has(f.name)) {
        filesWithErrors.push(f.name);
      }
    });

    const unexpectedFiles = this.unexpectedFiles;

    this.validationErrors = [];

    if (unexpectedFiles.length > 0) {
      unexpectedFiles.forEach((f) => {
        this.validationErrors.push(`"${f.name}" is not in the expected list`);
      });
    }

    if (filesWithErrors.length > 0) {
      this.validationErrors.push(
        `${filesWithErrors.length} file(s) failed validation — see individual errors below`,
      );
    }

    this.isFilesSelected =
      missingFiles.length === 0 &&
      filesWithErrors.length === 0 &&
      unexpectedFiles.length === 0 &&
      this.expectedFileNames.length > 0;

    if (missingFiles.length > 0) {
      const preview = missingFiles.slice(0, 5).join(', ');
      const remainder = missingFiles.length - 5;
      this.messageService.add({
        severity: 'error',
        summary: `${missingFiles.length} File(s) Missing`,
        detail: remainder > 0 ? `${preview}, and ${remainder} more` : preview,
        life: 8000,
      });
    }

    this.clampPageFirstIndex();

    if (this.isFilesSelected) {
      this.prepareUploadList();
    }
  }

  private async getPDFPageCount(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const typedArray = new Uint8Array(e.target?.result as ArrayBuffer);

          const CHUNK_SIZE = 8192;
          let text = '';
          for (let i = 0; i < typedArray.length; i += CHUNK_SIZE) {
            const chunk = typedArray.subarray(i, i + CHUNK_SIZE);
            text += String.fromCharCode(...chunk);
          }

          const countMatch = text.match(/\/Type\s*\/Pages[^]*?\/Count\s+(\d+)/);
          if (countMatch && countMatch[1]) {
            resolve(parseInt(countMatch[1], 10));
            return;
          }

          const pageMatches = text.match(/\/Type\s*\/Page[^s]/g);
          if (pageMatches) {
            resolve(pageMatches.length);
            return;
          }

          reject(new Error('Unable to determine page count'));
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read PDF file'));
      reader.readAsArrayBuffer(file);
    });
  }

  // ── Upload preparation ────────────────────────────────────────────────────────

  private prepareUploadList() {
    this.uploadedFiles = [];

    this.expectedFileNames.forEach((expectedName, index) => {
      const file = this.selectedFiles.find((f) => f.name === expectedName);
      if (!file) return;

      const urlData = this.presignedUrlsMap.get(expectedName);
      if (!urlData) return;

      const fileStatus: FileUploadStatus = {
        file: file,
        fileName: file.name,
        expectedFileName: expectedName,
        size: this.formatFileSize(file.size),
        sizeInBytes: file.size,
        position: index + 1,
        uploadUrl: urlData.upload_url,
        uploadFields: urlData.upload_fields,
        status: 'pending',
        progress: 0,
      };

      this.uploadedFiles.push(fileStatus);
    });
  }

  // ── Upload ────────────────────────────────────────────────────────────────────

  async startUpload() {
    if (!this.isFilesSelected || this.uploadedFiles.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Files',
        detail: 'Please select all required files first',
      });
      return;
    }

    this.uploadProgressService.startUpload([...this.uploadedFiles]);

    this.uploadProgressService.uploadState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        state.files.forEach((file, index) => {
          const localFile = this.uploadedFiles[index];
          if (file.status === 'pending' && localFile.status === 'error') {
            localFile.status = 'pending';
            localFile.progress = 0;
            localFile.errorMessage = undefined;
            this.processFile(localFile, index);
          }
        });
      });

    this.processUploadsInBackground();
  }

  // Runs uploads with a bounded concurrency instead of firing all of them at
  // once — at a few thousand files, unbounded parallelism was the thing
  // actually crashing the tab (see UPLOAD_CONCURRENCY below), not file size.
  private readonly UPLOAD_CONCURRENCY = 5;

  private processUploadsInBackground() {
    from(this.uploadedFiles)
      .pipe(
        mergeMap(
          (fileStatus, index) => {
            this.uploadProgressService.setCurrentFileIndex(index);
            return from(this.processFile(fileStatus, index)).pipe(
              catchError(() => of(null)), // one file's failure shouldn't stop the rest
            );
          },
          this.UPLOAD_CONCURRENCY,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe({
        complete: () => {
          const successCount = this.uploadedFiles.filter(
            (f) => f.status === 'success',
          ).length;
          const errorCount = this.uploadedFiles.filter(
            (f) => f.status === 'error',
          ).length;

          if (successCount === this.uploadedFiles.length) {
            this.messageService.add({
              severity: 'success',
              summary: 'Upload Complete',
              detail: `All ${successCount} files uploaded successfully!`,
            });
          } else if (successCount > 0) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Partial Upload',
              detail: `${successCount} of ${this.uploadedFiles.length} files uploaded. ${errorCount} failed.`,
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Upload Failed',
              detail: `All ${errorCount} files failed to upload.`,
            });
          }
        },
      });
  }

  // CHANGED: removed already-signed check (now done at selection time),
  // only runs basic PDF security check for eStamp
  private async processFile(
    fileStatus: FileUploadStatus,
    index: number,
  ): Promise<void> {
    try {
      fileStatus.status = 'validating';
      fileStatus.progress = 5;
      this.uploadProgressService.updateFileStatus(index, {
        status: 'validating',
        progress: 5,
      });

      // For eStamp only, run security check (no sign check needed)
      if (this.selectedServiceType === 'eStamp') {
        const validation =
          await this.pdfSecurityService.validateAndSanitizePDF(
            fileStatus.file,
          );
        if (!validation.isValid && !validation.isESigned) {
          throw new Error(validation.reason || 'PDF validation failed');
        }
      }

      fileStatus.progress = 20;
      this.uploadProgressService.updateFileStatus(index, { progress: 20 });

      fileStatus.status = 'uploading';
      this.uploadProgressService.updateFileStatus(index, {
        status: 'uploading',
      });

      // Uploads the original File/Blob directly — no base64 encode/decode
      // round-trip, so memory per in-flight file stays at ~1x its size
      // instead of ~2-3x.
      await this.uploadToPresignedUrl(fileStatus, (progress) => {
        fileStatus.progress = 20 + progress * 80;
        this.uploadProgressService.updateFileStatus(index, {
          progress: fileStatus.progress,
        });
      });

      fileStatus.status = 'success';
      fileStatus.progress = 100;
      this.uploadProgressService.updateFileStatus(index, {
        status: 'success',
        progress: 100,
      });

      this.messageService.add({
        severity: 'success',
        summary: 'Upload Successful',
        detail: `Position ${fileStatus.position}: "${fileStatus.fileName}" uploaded`,
      });
    } catch (error: any) {
      fileStatus.status = 'error';
      fileStatus.errorMessage = error.message || 'Upload failed';
      fileStatus.progress = 0;

      this.uploadProgressService.updateFileStatus(index, {
        status: 'error',
        errorMessage: fileStatus.errorMessage,
        progress: 0,
      });

      this.messageService.add({
        severity: 'error',
        summary: 'Upload Failed',
        detail: `Position ${fileStatus.position}: ${fileStatus.errorMessage}`,
        life: 6000,
      });
    }
  }

  private async uploadToPresignedUrl(
    fileStatus: FileUploadStatus,
    progressCallback?: (progress: number) => void,
  ): Promise<void> {
    const formData = new FormData();

    Object.keys(fileStatus.uploadFields).forEach((key) => {
      formData.append(key, fileStatus.uploadFields[key]);
    });
    formData.append('file', fileStatus.file, fileStatus.fileName);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && progressCallback) {
          progressCallback(event.loaded / event.total);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () =>
        reject(new Error('Network error during upload')),
      );
      xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

      xhr.open('POST', fileStatus.uploadUrl);
      xhr.send(formData);
    });
  }

  retryFile(index: number) {
    const fileStatus = this.uploadedFiles[index];
    fileStatus.status = 'pending';
    fileStatus.progress = 0;
    fileStatus.errorMessage = undefined;

    this.uploadProgressService.updateFileStatus(index, {
      status: 'pending',
      progress: 0,
      errorMessage: undefined,
    });

    this.processFile(fileStatus, index);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  goBackToExcel() {
    this.router.navigate(['/doqfy/bulk-upload-estamp/excel-upload']);
  }

  /**
   * Remove every attached file that isn't in the expected (Excel) list in one go,
   * leaving only the required files the user still needs to add.
   */
  removeAllUnexpectedFiles() {
    const namesToRemove = new Set(this.unexpectedFiles.map((f) => f.name));
    if (namesToRemove.size === 0) return;

    this.selectedFiles = this.selectedFiles.filter(
      (f) => !namesToRemove.has(f.name),
    );
    namesToRemove.forEach((name) => {
      this.selectedFileSizeMap.delete(name);
      this.fileValidationStatus.delete(name);
      this.fileValidationErrors.delete(name);
    });

    this.isFilesSelected = false;
    this.evaluateOverallValidity();
    this.cdr.detectChanges();
  }

  private setupRetryHandler() {
    this.uploadProgressService.uploadState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        state.files.forEach((stateFile, index) => {
          const localFile = this.uploadedFiles[index];
          if (
            stateFile.status === 'pending' &&
            localFile &&
            (localFile.status === 'error' || localFile.status === 'success')
          ) {
            localFile.status = 'pending';
            localFile.progress = 0;
            localFile.errorMessage = undefined;
            this.processFile(localFile, index);
          }
        });
      });
  }
}